import { lazy, Suspense, useEffect, useState } from 'react'
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { PageLayout } from '@/components/layout/PageLayout'
import { AppContextProvider } from '@/hooks/useAppContext'
import { WorkspaceRedirect, RedirectWithSearch } from '@/components/RouteRedirects'
import { IdentityProvider, useIdentity } from '@/hooks/useIdentity'
import { SignedOutRedirect } from '@/auth/SignedOutRedirect'
import { workspacesAPI } from '@/lib/api'
import Applications from '@/pages/Applications'
import TestsPage from '@/pages/Tests'
import TestDataSetsCatalog from '@/pages/TestDataSetsCatalog'
import TestDataSetEditor from '@/pages/TestDataSetEditor'
import TestDataBindingDetail from '@/pages/TestDataBindingDetail'
import TestDataBindingPending from '@/pages/TestDataBindingPending'
import { VariantCredentialsPanel } from '@/pages/VariantCredentialsPanel'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui'
import { useQueryParamState } from '@/hooks/useQueryParamState'
import Results from '@/pages/Results'
import RunReport from '@/pages/RunReport'
import Preferences from '@/pages/Preferences'
import SettingsPage from '@/pages/Settings'
import { AccountWrapper } from '@/pages/Settings/Account'
import {
  WorkspaceWrapper,
  WorkspaceGeneralRoute,
  WorkspaceMembersRoute,
  WorkspaceNotificationsSlackChannelsRoute,
  WorkspaceIntegrationsRoute,
  WorkspaceDangerRoute,
} from '@/pages/Settings/Workspace'
import {
  OrganizationWrapper,
  OrgGeneralRoute,
  OrgMembersRoute,
  OrgTeamsRoute,
  OrgWorkspacesRoute,
  OrgRunBudgetsRoute,
  OrgSingleSignOnRoute,
  OrgUsageRoute,
  OrgAuditRoute,
  OrgRecoveryRoute,
  OrgDangerRoute,
} from '@/pages/Settings/Organization'
import { AccountProfile } from '@/pages/AccountSettings/Profile'
import { AccountPasswordSessions } from '@/pages/AccountSettings/PasswordSessions'
import { AccountTokens } from '@/pages/AccountSettings/Tokens'
import { AccountNotifications } from '@/pages/AccountSettings/Notifications'
import { AccountDanger } from '@/pages/AccountSettings/Danger'
import Hints from '@/pages/Hints'
import { Onboarding } from '@/pages/Onboarding'
import { AccountDeletionPending } from '@/auth/screens/AccountDeletionPending'
import { NoWorkspaceAccess } from '@/pages/NoWorkspaceAccess'
import { IntegrationsError } from '@/pages/IntegrationsError'
// `/pages/Account` was retired by #547 PR 5 — `/account` now
// redirects to the Settings shell's Account → Profile tab. The
// component file is gone; the redirect lives inline below.
import { OrgSettingsRedirect } from '@/pages/Settings/OrgSettingsRedirect'
import { AdminPage } from '@/pages/Admin'
import { AdminLayout } from '@/pages/Admin/AdminLayout'
import { AdminPlaceholder } from '@/pages/Admin/Placeholder'
import { ConfigPage } from '@/pages/Admin/Config'
import { PricingPage } from '@/pages/Admin/Agents/Pricing'
import { AuditPage } from '@/pages/Admin/Audit'
import { OrgsPage } from '@/pages/Admin/Orgs'
import { AdminsPage } from '@/pages/Admin/Admins'
import { OperatorTokensPage } from '@/pages/Admin/Admins/OperatorTokens'
import { InvitationsPage } from '@/pages/Admin/Invitations'
import { UsersPage } from '@/pages/Admin/Users'
import { PromptsPage } from '@/pages/Admin/Agents/Prompts'
import { PromptDetailPage } from '@/pages/Admin/Agents/Prompts/Detail'
import { AgentModelsPage } from '@/pages/Admin/Agents/Models'
import { AgentBudgetsPage } from '@/pages/Admin/Agents/Budgets'
import { RagInspectorPage } from '@/pages/Admin/Agents/RAG'
import { ArtifactsPage } from '@/pages/Admin/Artifacts'
import { AboutPage } from '@/pages/Admin/About'
import { SystemFixturesPage } from '@/pages/Admin/SystemFixtures'
import { SystemFixtureDetailPage } from '@/pages/Admin/SystemFixtures/Detail'
import { ScheduledJobsPage } from '@/pages/Admin/ScheduledJobs'
import { ScheduledJobDetailPage } from '@/pages/Admin/ScheduledJobs/Detail'
import { RequireSuperadmin } from '@/auth/RequireSuperadmin'
import { Recover } from '@/pages/Recover'
import { AcceptInvitation } from '@/auth/screens/AcceptInvitation'
import { Login } from '@/auth/screens/Login'
import { Signup } from '@/auth/screens/Signup'
import { RequestPasswordReset } from '@/auth/screens/RequestPasswordReset'
import { ConfirmPasswordReset } from '@/auth/screens/ConfirmPasswordReset'
import { VerifyEmail } from '@/auth/screens/VerifyEmail'
import { VerifyEmailChange } from '@/auth/screens/VerifyEmailChange'
import { OAuthError } from '@/auth/screens/OAuthError'
import { DashboardSkeleton } from '@/pages/dashboard/components/DashboardSkeleton'
// PDF print page (#783) — eagerly imported among the static imports
// rather than `lazy()`d alongside the dashboard chunks below. See the
// comment in the lazy block for the rationale (headless-Chromium
// marker-wait race in dev).
import PrintSnapshotPage from '@/print/PrintSnapshotPage'
// VariantAutomationRedirect (#1184) — eagerly imported (not lazy)
// because it's a Navigate-only component with no chunk cost. Lives in
// its own module so the route-shape test (and any future caller) can
// import the production redirect directly.
import { VariantAutomationRedirect } from '@/pages/dashboard/VariantAutomationRedirect'

// Lazy-loaded dashboard chunks (#437). Splitting these out keeps the
// main bundle unaffected by the dashboard's reporting/visualization
// surface area until the user actually visits a /reports or
// /variants/:vid/dashboard route.
const VariantDashboard = lazy(() => import('@/pages/dashboard/VariantDashboard'))
const VariantCompare = lazy(() => import('@/pages/dashboard/VariantCompare'))
const RunDetail = lazy(() => import('@/pages/dashboard/RunDetail'))
const ReportsRedirect = lazy(() => import('@/pages/dashboard/ReportsRedirect'))
// PDF print page (#783) — NOT lazy-loaded; see the static import at
// the top of this file. Headless Chromium hits the print route with a
// marker-wait budget (`PRINT_READY_TIMEOUT_MS` in `htmlToPdf.ts`); in
// dev mode, Vite's on-demand compile of a lazy chunk on first cold
// hit can race that budget. By the time the chunk lands and React
// mounts PrintSnapshotPage, the marker timeout has fired and Chromium
// has fallen back to the empty `<Suspense fallback>` DOM — producing
// a blank PDF. Eager-loading eliminates the chunk-load gap; the print
// route is never visited by humans on the live site, so there's no
// bundle-size or user-perceived load-time cost.

// Lazy-loaded BI surface (#482, Slice C; relocated to admin-tier in
// #429 Wave 3 Track E). Echarts is ~600KB gzipped; keeping it off
// the main bundle means non-superadmins never pay the chunk cost
// (only the admin route mounts this).
const Analytics = lazy(() => import('@/pages/Admin/Analytics/Analytics'))

/** Back-compat redirect for the old per-run drill-down page. The
 * drill-down now lives in a right Drawer over the Analytics page,
 * hosted by the `?run=<id>` URL param (#1586). Any surviving deep
 * link to `/admin/analytics/runs/:runId` (bookmarks, copied URLs)
 * lands on the same view via a one-hop redirect. */
function AnalyticsRunRedirect() {
  const { runId } = useParams<{ runId: string }>()
  return <Navigate to={`/admin/analytics?run=${encodeURIComponent(runId ?? '')}`} replace />
}

/** Redirects any non-workspace-scoped path to its workspace-scoped
 * equivalent under the active workspace. Examples:
 *   /                  → /workspaces/<active>/results
 *   /applications      → /workspaces/<active>/applications
 *   /test-plan         → /workspaces/<active>/test-plan
 *
 * Active workspace comes from useAppContext, which resolved
 * /api/workspaces/me at boot. This component renders nothing — just
 * the redirect.
 *
 * **Defense against the prepend-loop class.** If the incoming path
 * already starts with `/workspaces/`, prepending another
 * `/workspaces/<id>` segment produces a runaway redirect chain
 * (`/workspaces/<id>/workspaces/<id>/.../foo`). That happens when an
 * inner workspace-scoped URL targets a route that doesn't exist —
 * e.g. an email link to `/workspaces/<id>/settings/notifications`
 * before the in-app redirect for that path existed. The catch-all
 * `*` route fires for the unmatched tail, this redirect prepends
 * again, the next render fires `*` again, ad infinitum.
 *
 * Guard: if the location is already a workspace path, bounce to the
 * active workspace's Runs page instead of layering more
 * `/workspaces/<id>/` prefixes. That trades the "preserve the
 * sub-path" affordance (which is broken anyway — the sub-path didn't
 * match any route) for a stable landing.
 */
/** /test-data nav target. Two tabs over distinct scopes:
 *
 *   - **Data Sets** — the unified, workspace-scoped Test Data Sets
 *     catalog (#1263; the legacy four-mode Shared Data Set screen is
 *     retired, boot-time migration converts existing data).
 *   - **Credentials** — the variant-scoped per-role login identities.
 *     This surface was inadvertently dropped when #1263 repointed
 *     `/test-data` to the catalog (the old page co-hosted both
 *     credentials and shared sets; the cutover evicted credentials as
 *     collateral). Restored here as a sibling tab — credentials are a
 *     separate system the redesign explicitly intended to keep.
 *
 * The active tab persists in `?tab=` (default `sets` strips the param),
 * so the per-app "add credentials" CTA can deep-link `?tab=credentials`. */
function TestDataSurface() {
  const [rawTab, setTab] = useQueryParamState('tab', 'sets')
  // Coerce unexpected query values (typo, stale link, manual edit) to the
  // default — an unmatched `value` renders no TabsContent (blank page).
  const tab = rawTab === 'credentials' ? 'credentials' : 'sets'
  return (
    <Tabs defaultValue="sets" value={tab} onValueChange={setTab} className="h-full flex flex-col">
      <TabsList className="justify-start">
        <TabsTrigger value="sets">Data Sets</TabsTrigger>
        <TabsTrigger value="credentials">Credentials</TabsTrigger>
      </TabsList>
      <TabsContent value="sets" className="flex-1 min-h-0 mt-0">
        <TestDataSetsCatalog />
      </TabsContent>
      <TabsContent value="credentials" className="flex-1 min-h-0 mt-0 overflow-y-auto">
        <VariantCredentialsPanel />
      </TabsContent>
    </Tabs>
  )
}

/** Matrix-editor route — direct mount, no flag gate (#1263). */
function TestDataSetEditorGate() {
  return <TestDataSetEditor />
}

/** Binding detail page — direct mount, no flag gate (#1263). */
function TestDataBindingDetailGate() {
  return <TestDataBindingDetail />
}

/** Pending-changes view — direct mount, no flag gate (#1263). */
function TestDataBindingPendingGate() {
  return <TestDataBindingPending />
}

/* `WorkspaceRedirect` and `RedirectWithSearch` moved to their own module
 * (#2175 slice 5). The redirect test had copied them verbatim because
 * importing this file bootstraps every provider; the copy then drifted the
 * first time a destination changed. Now both import the same definition. */

/** /account redirect (#547 PR 5). The legacy single-page
 * `/account` route is gone; its content moved into the Settings
 * shell's Account section. Bookmarks / external links to /account
 * land here and bounce to the same content on the workspace-scoped
 * Settings page.
 *
 * Earliest workspace by createdAt — matches the picker's default-
 * workspace rule. Falls back to /onboarding when the user has no
 * workspaces (zero-org case the IdentityGate would route there
 * anyway). */
function AccountRedirect() {
  const { state } = useIdentity()
  const [defaultWorkspaceId, setDefaultWorkspaceId] = useState<string | null | undefined>(
    undefined,
  )

  useEffect(() => {
    void workspacesAPI
      .list()
      .then((list) => {
        if (list.length === 0) {
          setDefaultWorkspaceId(null)
          return
        }
        const earliest = [...list].sort((a, b) =>
          a.createdAt.localeCompare(b.createdAt),
        )[0]!
        setDefaultWorkspaceId(earliest._id)
      })
      .catch(() => setDefaultWorkspaceId(null))
  }, [])

  if (state.status !== 'ready' || defaultWorkspaceId === undefined) {
    return null
  }
  if (!defaultWorkspaceId) {
    return <Navigate to="/onboarding" replace />
  }
  return (
    <Navigate
      to={`/workspaces/${defaultWorkspaceId}/settings?tab=account.profile`}
      replace
    />
  )
}

/** Legacy `/admin/prompts/:id` → `/admin/models/prompts/:id` redirect.
 * Prompt management lives under the renamed Models sub-nav (#1281); this
 * component preserves the prompt id so a bookmarked deep-link to a
 * specific prompt still resolves to the same prompt's detail page at
 * the new URL. */
function LegacyPromptDetailRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/admin/models/prompts/${id ?? ''}`} replace />
}

/** Legacy `/admin/agents/prompts/:id` → `/admin/models/prompts/:id`
 * redirect. The whole Agents tier was renamed to Models in #1281;
 * the prompt-id-preserving form of the redirect exists alongside the
 * generic `/admin/agents/prompts → /admin/models/prompts` redirect for
 * the same id-preservation reason as the older
 * `/admin/prompts → /admin/agents/prompts` redirect above. */
function LegacyModelPromptDetailRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/admin/models/prompts/${id ?? ''}`} replace />
}

// `VariantAutomationRedirect` lives in its own module (imported at
// the top of this file) — see the route registration below for where
// it mounts. The legacy `/workspaces/<wid>/variants/<vid>/automation`
// URL handler bounces to the App Integrations tab's drawer (#1184).
// Extracted into its own module so the route-shape test imports the
// production redirect directly rather than maintaining a
// hand-replicated copy here (Copilot review on PR #1186).

/** Identity gate. Sits above every post-auth route. Reads the
 * IdentityProvider state and decides whether to:
 *
 *   - render the requested route (signed in + has orgs)
 *   - bounce to Create account or Sign in (no session; see
 *     `SignedOutRedirect`)
 *   - bounce to /onboarding (signed in + no orgs, except when
 *     they're already on /onboarding)
 *
 * Loading and error states get a thin skeleton so the user doesn't
 * see a flash of the wrong page while /api/me resolves. */
function IdentityGate() {
  const { state } = useIdentity()
  const location = useLocation()

  if (state.status === 'loading') {
    return (
      <div className="min-h-dvh flex items-center justify-center text-muted-foreground text-sm">
        Loading…
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center gap-2 px-4">
        <p className="text-sm text-destructive">Couldn't reach the server.</p>
        <p className="text-xs text-muted-foreground">{state.message}</p>
      </div>
    )
  }

  if (state.status === 'unauthenticated') {
    return <SignedOutRedirect />
  }

  // Account scheduled for deletion (#1748): a re-logged-in user sees the
  // explicit restore prompt instead of the app — highest priority, before
  // onboarding/workspace routing. Recovery is deliberate (never automatic).
  if (state.identity.deletionPending) {
    return <AccountDeletionPending recoverableUntil={state.identity.recoverableUntil} />
  }

  // Soft gate: send needsOnboarding users to /onboarding unless they're
  // already there. Anywhere else (including /workspaces/...) 404s gracefully
  // via the backend's 404/410 surface.
  //
  // #2454 — `needsOnboarding` now also covers somebody who has an
  // organization and no plan, so this gate holds people it never used to:
  // a customer whose payment was interrupted, or whose allowance ran out.
  // Those people need a way OUT, and without these exceptions the screen is
  // a trap. One of them is told in so many words to change their email in
  // settings, and settings would be unreachable.
  //
  // Deliberately a prefix match on a small list rather than an allowlist of
  // exact paths: account settings is several routes, and a gate that let
  // somebody onto the first and bounced them off the second would be worse
  // than one that let them nowhere.
  // `/account` is a redirect shim: it bounces to
  // `/workspaces/<id>/settings...`, which is where the email actually lives.
  // An earlier version listed only the shim, so the escape landed on a
  // redirect that failed this check and came straight back here. The gate was
  // then the trap it exists not to be, and one of its own screens tells
  // somebody to go and change their email. Codex caught it on #2459.
  //
  // So the settings surface itself is the exception, not the shim. That does
  // not weaken the gate: it exists to stop a customer WORKING without a plan,
  // not to stop them reading their own settings, and every route that runs a
  // test is still held.
  const onAnEscape =
    ['/onboarding', '/account', '/preferences'].some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    )
    || /^\/workspaces\/[^/]+\/settings(\/|$)/.test(location.pathname)

  // Two separate reasons to hold somebody, combined here rather than merged
  // upstream. `needsOnboarding` means they have no organization;
  // `billing` means an organization they have cannot work. Keeping them
  // apart matters because the onboarding screen provisions off the first one,
  // and widening it would re-provision an existing customer whose only
  // problem is an unpaid plan.
  const heldForBilling = (state.identity.billing ?? 'fine') !== 'fine'
  if ((state.identity.needsOnboarding || heldForBilling) && !onAnEscape) {
    return <Navigate to="/onboarding" replace />
  }

  // Org member with no workspace access — e.g. someone who just
  // accepted an org invitation. Invitations grant ORG membership only;
  // workspace access is a separate admin grant (ADR 0010's two-tier
  // model). Render a dedicated landing instead of letting
  // WorkspaceRedirect / useAppContext crash trying to resolve a
  // workspace this user would 403 on. `/admin` stays reachable as a
  // superadmin escape hatch (a superadmin without workspace access
  // can still run the admin shell).
  const hasWorkspaceAccess =
    Object.keys(state.identity.workspaceRoles ?? {}).length > 0
  if (
    !state.identity.needsOnboarding &&
    !hasWorkspaceAccess &&
    !location.pathname.startsWith('/admin')
  ) {
    return <NoWorkspaceAccess />
  }

  return <Outlet />
}

/** Wraps post-identity routes with workspace context. Onboarding sits
 * BETWEEN identity and workspace context — it has identity (the user
 * exists) but no workspace yet (that's what they're about to create). */
function WithWorkspaceContext() {
  return (
    <AppContextProvider>
      <Outlet />
    </AppContextProvider>
  )
}

function App() {
  return (
    <Routes>
      {/* Auth screens — public, no identity provider. The verify-email
          and reset-confirm routes are click targets of emails Better
          Auth sends, so they must be reachable by direct URL. */}
      {/* Login wraps in its own IdentityProvider so the post-sign-in
          handler can call refreshIdentity() before navigating —
          without that, the destination route renders blank against
          the still-unauthenticated React-side identity context
          until a manual refresh. Same pattern as
          /accept-invitation below. */}
      <Route
        path="/auth/login"
        element={
          <IdentityProvider>
            <Login />
          </IdentityProvider>
        }
      />
      {/* Signup wraps in its own IdentityProvider for the same reason
          as Login: with email off, signing up signs the person in and
          the screen calls refreshIdentity() before navigating on. */}
      <Route
        path="/auth/signup"
        element={
          <IdentityProvider>
            <Signup />
          </IdentityProvider>
        }
      />
      <Route path="/auth/reset" element={<RequestPasswordReset />} />
      <Route path="/auth/reset-confirm" element={<ConfirmPasswordReset />} />
      <Route path="/auth/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/verify-email-change" element={<VerifyEmailChange />} />
      {/* Auth error landing — replaces better-auth's default
          /api/auth/error page (which has an "Ask AI" link + reads
          as a debug stack). Wired via betterAuth({ onAPIError:
          { errorURL } }) on the server + errorCallbackURL on the
          signIn.social call sites for defense in depth. */}
      <Route path="/auth/error" element={<OAuthError />} />

      {/* Print routes for PDF generation (#783). Top-level — no
          PageLayout, no IdentityProvider — the headless browser
          fetches a public snapshot endpoint with the opaque snapshot
          id as the cap. Direct human visits work too.
          PrintSnapshotPage is eagerly imported (not `lazy()`d) so the
          first-cold-load case can't race the headless Chromium's
          marker-wait budget — see the import comment above. */}
      <Route path="/print/snapshot/:id" element={<PrintSnapshotPage />} />

      {/* Invitation accept page — public surface that may show
          "sign in to accept" if the invitee isn't authenticated.
          Wraps in its own IdentityProvider (without the IdentityGate)
          so the page can call refreshIdentity() after a successful
          accept — without that, the post-accept navigate lands on
          a route whose IdentityProvider has stale "no orgs" state
          and the destination renders blank until the user manually
          refreshes. The page still handles its own session-aware
          state machine (signed-in / wrong email / not signed in)
          via the IdentityProvider's state.status. */}
      <Route
        path="/accept-invitation/:invitationId"
        element={
          <IdentityProvider>
            <AcceptInvitation />
          </IdentityProvider>
        }
      />

      {/* Everything else needs identity. IdentityProvider fetches
          /api/me once; the gate inside redirects on the three
          terminal states. */}
      <Route
        element={
          <IdentityProvider>
            <IdentityGate />
          </IdentityProvider>
        }
      >
        {/* Identity-only routes — onboarding, account, org settings,
            user preferences. None need a workspace context. */}
        <Route path="/onboarding" element={<Onboarding />} />
        {/* /integrations-error — workspace-less fallback landing for
            OAuth callback failures the server couldn't pin to a
            workspaceId (#1123). Identity-only (no workspace context). */}
        <Route path="/integrations-error" element={<IntegrationsError />} />
        {/* /account — retired in favor of the Settings shell's
            Account section (#547 PR 5). Redirect preserves any
            external links / bookmarks that still target the legacy
            URL. `AccountRedirect` (below) bounces to the user's
            workspace-scoped /settings page if a default workspace
            exists, falling back to /onboarding for users who have
            no orgs at all. */}
        <Route path="/account" element={<AccountRedirect />} />
        {/* /orgs/:orgId/settings retired in favor of the unified
            Settings page at /workspaces/:wsId/settings (#547 PR 2).
            Redirects to the new page's Organization · General tab,
            using the user's active workspace under the requested
            org as the URL's workspace context. */}
        <Route path="/orgs/:orgId/settings" element={<OrgSettingsRedirect />} />
        <Route path="/recover" element={<Recover />} />
        <Route path="/preferences" element={<Preferences />} />

        {/* /admin/* — identity-tier admin shell (#429 Wave 1 Track C).
            Sibling of the workspace route, NOT a child — superadmins
            view pricing / runtime config / cross-org analytics
            without first picking a workspace. PageLayout adapts to
            the missing workspace context by hiding workspace-scoped
            controls (org/ws picker, app/variant dropdown, workspace
            nav entries); the sidebar's Admin + admin-Analytics
            entries stay visible in both modes. Sub-routes
            (`/admin/config`, `/admin/pricing`, etc.) are advertised
            by the index page until each Wave 3 track lands a real
            screen — Wave 3 Track E (Analytics, this PR) is the
            first; remaining tracks (D Runtime Config, F Pricing)
            swap their `<Route path="*" />` placeholder for a real
            screen by inserting more-specific routes ABOVE the
            catch-all (React Router matches more-specific first).

            `<RequireSuperadmin>` wraps the shell so a non-superadmin
            deep-linking to /admin/<anything> sees a forbidden stub
            instead of the admin chrome (#429 Wave 2 — PR #521 Copilot
            review). Server-side `requireSuperadmin` middleware on
            `/api/admin/*` routes (today: `/api/admin/bi/*` for the
            analytics tier) is the actual data-protection boundary;
            this gate is UX + defense in depth. */}
        <Route element={<RequireSuperadmin />}>
          {/* #1528: the admin tier gets its own shell — the grouped
              AdminRail in place of the workspace Sidebar — instead of the
              shared PageLayout. The /admin index keeps the discovery grid
              as the rail's overview pane. */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminPage />} />
            {/* Wave 3 Track D / #410 — runtime config. Registered
                ABOVE the catch-all so React Router matches the
                specific path before falling through. */}
            <Route path="config" element={<ConfigPage />} />
            {/* Wave 3 Track G — audit-log reader. Same placement
                rationale (ABOVE the catch-all). Read-only over the
                `auditEvents` collection Wave 2 already populates. */}
            <Route path="audit" element={<AuditPage />} />
            {/* Admin-tier org provisioning (create-org is
                a privileged tenant-spawn, not a self-serve affordance).
                Same placement rationale as the sections above
                (ABOVE the catch-all). */}
            <Route path="orgs" element={<OrgsPage />} />
            {/* Superadmin grant management (#1022 Slice 1). Replaces
                the env-only allowlist with a DB-backed grant collection
                managed via this UI. Server-side
                `/api/admin/superadmin-grants` is gated by
                `requireSuperadmin`. */}
            <Route path="admins" element={<AdminsPage />} />
            {/* Operator Tokens tab under Admins (#1573). Capability-
                scoped credentials for tools outside the app; minting is
                superadmin-gated server-side. Sibling route under the
                People · Operator Tokens sub-nav. */}
            <Route path="admins/operator-tokens" element={<OperatorTokensPage />} />
            {/* Global Users admin surface (#1023 Slice 2; meta
                #1021). Cross-tenant directory + per-user membership
                graph. Read-only in v1 (Tier-2 suspend / impersonate
                land later). Server-side `/api/admin/users` is gated
                by `requireSuperadmin` and audits both endpoints
                (`user.list_viewed`, `user.detail_viewed`) — the
                actual data boundary. */}
            <Route path="users" element={<UsersPage />} />
            {/* Cross-org invitations directory (#1024 Slice 3 of
                #1021). Read-only v1: pending / accepted / expired
                invitations across every org, filterable by status,
                org, and free-text invitee + inviter email patterns.
                Server-side `/api/admin/invitations` is gated by
                `requireSuperadmin` and audits every read. */}
            <Route path="invitations" element={<InvitationsPage />} />
            {/* SuperAdmin → Agents sub-nav: Prompts, Models, Pricing,
                Budgets, RAG. All are agent-behaviour / agent-cost
                config; grouping them under one parent keeps the
                discovery surface honest. Each child renders its own
                page; `/admin/agents` itself redirects to the most-
                visited child (Prompts) — operators landing on the
                parent URL almost always want the prompt directory.

                Backwards-compat: the old `/admin/prompts[/:id]` and
                `/admin/pricing` URLs are 1:1-redirected to their new
                `/admin/agents/*` paths so bookmarks and pre-merge links
                keep working — see the explicit `<Navigate>` entries
                below this block. (Models + Budgets had no dedicated
                URLs pre-reshape — they were sections inside
                `/admin/config` — so no redirects needed for those.) */}
            {/* #1281 — Models tier (was Agents). The parent path lands
                on Assignment, where each agent's model + temperature
                controls now sit alongside that model's live health
                (status, fix message when failing, per-row Check now)
                and a read-only embedding-model row — config and health
                on one screen (#1281 follow-up; the separate Catalog page
                was removed). The sibling tabs (Prompts, Pricing,
                Budgets, Agent Memory) keep their behaviour at the new
                URLs; the old `/admin/agents/*` URLs redirect below so
                bookmarks survive. */}
            <Route path="models" element={<Navigate to="/admin/models/assignment" replace />} />
            <Route path="models/prompts" element={<PromptsPage />} />
            <Route path="models/prompts/:id" element={<PromptDetailPage />} />
            <Route path="models/assignment" element={<AgentModelsPage />} />
            <Route path="models/catalog" element={<PricingPage />} />
            {/* Renamed from models/pricing: the tab outgrew "Pricing" once
                #1932 added capability, context and provenance columns. Kept
                as a redirect rather than deleted — same courtesy every other
                retired route here gets. */}
            <Route path="models/pricing" element={<Navigate to="/admin/models/catalog" replace />} />
            <Route path="models/budgets" element={<AgentBudgetsPage />} />
            <Route path="models/rag" element={<RagInspectorPage />} />
            {/* Pre-#1281 paths — preserved as redirects so existing
                bookmarks keep resolving. */}
            <Route path="agents" element={<Navigate to="/admin/models" replace />} />
            <Route path="agents/prompts" element={<Navigate to="/admin/models/prompts" replace />} />
            <Route path="agents/prompts/:id" element={<LegacyModelPromptDetailRedirect />} />
            <Route path="agents/models" element={<Navigate to="/admin/models/assignment" replace />} />
            <Route path="agents/pricing" element={<Navigate to="/admin/models/catalog" replace />} />
            <Route path="agents/budgets" element={<Navigate to="/admin/models/budgets" replace />} />
            <Route path="agents/rag" element={<Navigate to="/admin/models/rag" replace />} />
            <Route path="artifacts" element={<ArtifactsPage />} />
            {/* About: version, newer release, license and support in one place. */}
            <Route path="about" element={<AboutPage />} />
            {/* System Fixtures viewer (#914). Read-only catalog of the
                pre-canned Playwright login fixtures the planner can
                auto-match. List view consumes the public catalog
                endpoint; detail view hits the admin-tier endpoint that
                returns the heavy `specTemplate` field. Same placement
                rationale as the sections above (ABOVE the catch-all
                so React Router resolves the specific path first). */}
            <Route path="system-fixtures" element={<SystemFixturesPage />} />
            <Route path="system-fixtures/:id" element={<SystemFixtureDetailPage />} />
            {/* Scheduled Jobs admin surface (#1278 / #1280). Catalog
                list + per-job detail with three tabs (Overview, Run
                history, Configuration). Server-side
                `/api/admin/scheduled-jobs` is gated by
                `requireSuperadmin` — same trust-boundary pattern as
                the rest of /admin/*. */}
            <Route path="scheduled-jobs" element={<ScheduledJobsPage />} />
            <Route path="scheduled-jobs/:jobId" element={<ScheduledJobDetailPage />} />
            {/* Legacy redirects → Agents sub-nav reshape. `/admin/prompts`
                (#574 PR 3) and `/admin/pricing` (Admin IA reshape) keep
                resolving. React Router resolves `:id` for the inner
                Navigate so the redirected prompt path preserves the id. */}
            <Route path="prompts" element={<Navigate to="/admin/models/prompts" replace />} />
            <Route path="prompts/:id" element={<LegacyPromptDetailRedirect />} />
            <Route path="pricing" element={<Navigate to="/admin/models/catalog" replace />} />
            {/* Cross-org BI analytics (#482, Slice C; relocated from
                /workspaces/:wid/analytics in #429 Wave 3 Track E).
                Lazy-loaded via React.lazy — echarts (~600KB gzipped)
                stays off the main bundle, and non-superadmins never
                hit this code path (the `<RequireSuperadmin>` wrapper
                above gates the whole `/admin/*` tree). Server-side
                `/api/admin/bi/*` is gated by `requireSuperadmin` —
                the actual data boundary. */}
            <Route
              path="analytics"
              element={
                <Suspense fallback={<DashboardSkeleton />}>
                  <Analytics />
                </Suspense>
              }
            />
            {/* Old per-run page → Drawer over Analytics (#1586). */}
            <Route path="analytics/runs/:runId" element={<AnalyticsRunRedirect />} />
            {/* Catch-all: any unknown `/admin/<segment>` keeps the user
                inside the admin shell with a placeholder explaining
                which Wave 3 track will populate it, instead of falling
                through to the workspace fallback. PR #520 Copilot
                review. MUST stay last — React Router matches
                more-specific routes first. */}
            <Route path="*" element={<AdminPlaceholder />} />
          </Route>
        </Route>

        {/* Everything below also needs workspace context. */}
        <Route element={<WithWorkspaceContext />}>
          <Route path="/workspaces/:workspaceId" element={<PageLayout />}>
            {/* The workspace lands on Runs (#2158 slice 3b). */}
            <Route index element={<Navigate to="results" replace />} />
            <Route path="applications" element={<Applications />} />
            <Route path="results" element={<Results />} />
            <Route path="tests" element={<TestsPage />} />
            {/* /test-plan + /test-suites retired in favor of the
                unified /tests page (#542). Hard redirects per the
                established precedent for prior route retirements
                (lessons → hints, templates → Runs,
                analytics → /admin/analytics). */}
            <Route path="test-plan" element={<Navigate to="../tests" replace />} />
            <Route path="test-suites" element={<Navigate to="../tests" replace />} />
            {/* Test Data — the legacy variant-scoped Shared Data Set
                surface (`<TestData />`) and the new workspace-scoped
                unified surface (`<TestDataSetsCatalog />` +
                `<TestDataSetEditor />`) live behind a feature flag
                (`testDataUnify`). `<TestDataSurface>` reads the flag
                from `useIdentity()` and dispatches; the editor is a
                child route so its layout (sticky header + full-height
                table) gets the full main pane. */}
            <Route path="test-data" element={<TestDataSurface />} />
            <Route path="test-data/:setId" element={<TestDataSetEditorGate />} />
            {/* Binding detail (#1258 Slice 3). Hosts the data mapper
                + binding-config panel for one (setId, scriptId) pair.
                Same feature-flag gate as the editor. */}
            <Route
              path="test-data/:setId/bindings/:scriptId"
              element={<TestDataBindingDetailGate />}
            />
            {/* Pending-changes resolution screen (#1262 slice 4). Same
                feature-flag gate as the binding detail page. */}
            <Route
              path="test-data/:setId/bindings/:scriptId/pending"
              element={<TestDataBindingPendingGate />}
            />
            <Route path="hints" element={<Hints />} />
            {/* Back-compat redirect for the pre-#287 path. Bookmarks /
                external links from before the Lessons → Hints rename
                still resolve. Safe to remove once we're confident no
                stale links remain. */}
            <Route path="lessons" element={<Navigate to="../hints" replace />} />
            {/* /runner retired (#2175 slice 5). It was the one list that
                held every pool's settings at once, which is what let an
                edit made for a manual run reach a schedule or a CI rule.
                Each pool has its own list now: manual behind the three
                buttons on Runs, the scheduler's under Scheduled, CI's
                inside its connection. /templates redirected here rather
                than to the runner, so both bookmarks land on Runs. */}
            <Route path="runner" element={<Navigate to="../results" replace />} />
            <Route path="templates" element={<Navigate to="../results" replace />} />
            {/* Settings — cards index + per-section pill sub-nav
                (#851). Each section wrapper renders sub-nav + Outlet;
                tab content components are mounted as child routes via
                small adapters defined in each wrapper file. Mirrors
                the /admin/agents nested-route + AgentsSubNav pattern. */}
            <Route path="settings">
              <Route index element={<SettingsPage />} />

              {/* Convenience alias for the email-link target. The
                  `slack.integration_broken` admin notification (and
                  any future notification deep-link that names the
                  Notifications surface) points at
                  `/workspaces/<id>/settings/notifications`. Without
                  this route the URL falls through to the outer
                  catch-all `<WorkspaceRedirect>` which prepends
                  another `/workspaces/<id>/` segment to the same
                  path on every render — runaway redirect loop.

                  After #1153 the workspace's standalone Notifications
                  page no longer exists — Slack + GitHub provider
                  connections live as sibling cards on the consolidated
                  Integrations tab. The redirect lands the email
                  recipient on that tab, where the Slack card carries
                  the connection lifecycle the notification was
                  pointing at.

                  `to="../workspace/integrations"` is deliberately
                  parent-relative (NOT `workspace/integrations`).
                  React Router's `<Navigate>` resolves a relative
                  `to` against the CURRENT pathname, so a bare
                  `workspace/integrations` from inside
                  `settings/notifications` would append to that
                  segment and produce
                  `settings/notifications/workspace/integrations`.
                  The `..` walks one segment up to the settings
                  parent first. */}
              <Route
                path="notifications"
                element={<Navigate to="../workspace/integrations" replace />}
              />

              <Route path="account" element={<AccountWrapper />}>
                <Route index element={<Navigate to="profile" replace />} />
                <Route path="profile" element={<AccountProfile />} />
                <Route path="password" element={<AccountPasswordSessions />} />
                <Route path="tokens" element={<AccountTokens />} />
                <Route path="notifications" element={<AccountNotifications />} />
                <Route path="danger" element={<AccountDanger />} />
              </Route>

              <Route path="workspace" element={<WorkspaceWrapper />}>
                <Route index element={<Navigate to="general" replace />} />
                <Route path="general" element={<WorkspaceGeneralRoute />} />
                <Route path="members" element={<WorkspaceMembersRoute />} />
                {/* #1153: `workspace/integrations` is the canonical
                    consolidated tab. Two sibling cards inside (GitHub
                    + Slack) replace the earlier CI/CD + Notifications
                    pair. */}
                <Route path="integrations" element={<WorkspaceIntegrationsRoute />} />
                {/* Channel-picker sub-route survives the consolidation
                    — the post-OAuth Slack wizard flow still lands here
                    to let the operator pick channels (the picker host
                    page rendered in `NotificationsSlackChannelPicker`).
                    Path kept stable so the backend `slackOAuth`
                    callback URLs and the picker's own internal links
                    don't have to change. */}
                <Route
                  path="notifications/slack/channels"
                  element={<WorkspaceNotificationsSlackChannelsRoute />}
                />
                {/* #1153 legacy alias: `workspace/ci-cd` (the brief
                    rename from PR #1145) redirects to the new
                    canonical `workspace/integrations`. Preserves the
                    sibling search string via `RedirectWithSearch` so
                    in-flight GitHub OAuth callbacks
                    (`?github=connected` and the error code variants)
                    still surface the right banner on landing. */}
                <Route path="ci-cd" element={<RedirectWithSearch to="../integrations" />} />
                {/* #1153 legacy alias: the standalone Notifications
                    page was absorbed into Integrations. Slack OAuth
                    callbacks emitted before this consolidation still
                    target `workspace/notifications?slackError=...`;
                    `RedirectWithSearch` forwards them to the canonical
                    Integrations tab so the toast surfaces correctly. */}
                <Route path="notifications" element={<RedirectWithSearch to="../integrations" />} />
                <Route path="danger" element={<WorkspaceDangerRoute />} />
                {/* Legacy `auth-fixtures` redirect — the tab was
                    removed alongside #570 (closed without a
                    workspace-level auth-fixtures product decision).
                    The Settings index's ?tab=workspace.auth-fixtures
                    back-compat regex still maps to /workspace/auth-fixtures,
                    so without this stub, old bookmarks would land on
                    an empty Outlet. Redirect to General so old links
                    stay alive without prepending another /workspaces/
                    segment via the parent's catch-all. */}
                <Route path="auth-fixtures" element={<Navigate to="../general" replace />} />
              </Route>

              <Route path="organization" element={<OrganizationWrapper />}>
                <Route index element={<Navigate to="general" replace />} />
                <Route path="general" element={<OrgGeneralRoute />} />
                <Route path="members" element={<OrgMembersRoute />} />
                <Route path="teams" element={<OrgTeamsRoute />} />
                <Route path="workspaces" element={<OrgWorkspacesRoute />} />
                <Route path="single-sign-on" element={<OrgSingleSignOnRoute />} />
                <Route path="run-budgets" element={<OrgRunBudgetsRoute />} />
                <Route path="usage" element={<OrgUsageRoute />} />
                <Route path="audit" element={<OrgAuditRoute />} />
                <Route path="recovery" element={<OrgRecoveryRoute />} />
                <Route path="danger" element={<OrgDangerRoute />} />
              </Route>
            </Route>
            {/* Customer regression dashboard (#437). Lazy-loaded
                via React.lazy so the main bundle stays unaffected.
                Both the variant dashboard and the per-run drill-down
                must be reachable via direct deep-link — the URL is
                the source of truth, NOT useAppContext.selectedVariantId.
                /reports redirects to the active variant's dashboard. */}
            <Route
              path="reports"
              element={
                <Suspense fallback={<DashboardSkeleton />}>
                  <ReportsRedirect />
                </Suspense>
              }
            />
            <Route
              path="variants/:variantId/dashboard"
              element={
                <Suspense fallback={<DashboardSkeleton />}>
                  <VariantDashboard />
                </Suspense>
              }
            />
            {/* Run comparison (#747 Slice 3) — release/date diff.
                Sibling of the dashboard; deep-linkable, the selection
                lives in the URL search params. */}
            <Route
              path="variants/:variantId/compare"
              element={
                <Suspense fallback={<DashboardSkeleton />}>
                  <VariantCompare />
                </Suspense>
              }
            />
            {/* Variant Automation page retired in #1184 (Slice 1 of
                meta #1183). The standalone page hosted per-variant
                trigger rules + Slack notification overrides; that
                surface moved into a right-side drawer on the parent
                App's Integrations tab so the user never loses the App
                configuration context. Existing bookmarks redirect to
                that drawer (variant pre-selected via
                `?variantConfig=<vid>`); the lookup of the variant's
                parent app happens inside `VariantAutomationRedirect`
                from the in-memory variant graph. Chose redirect over
                a 404 so any in-flight bookmark / email link lands on
                the same destination the user wanted. */}
            <Route
              path="variants/:variantId/automation"
              element={<VariantAutomationRedirect />}
            />
            <Route
              path="variants/:variantId/runs/:runId"
              element={
                <Suspense fallback={<DashboardSkeleton />}>
                  <RunDetail />
                </Suspense>
              }
            />
            {/* Wraps the Playwright HTML report in an iframe so it
                inherits the top nav + left sidebar instead of opening
                as a barren standalone page. Same origin, session
                cookie auth — see RunReport.tsx for the rationale. */}
            <Route path="runs/:runId/report" element={<RunReport />} />
            {/* The cross-run BI analytics route used to live here
                (#482, Slice C). #429 Wave 3 Track E relocated it to
                `/admin/analytics` (superadmin-only, cross-org).
                Stale `/workspaces/:wid/analytics` URLs need an
                EXPLICIT handler — without one, the catch-all
                `WorkspaceRedirect` below prepends `/workspaces/<wid>`
                to the current pathname and produces a recursively
                growing `/workspaces/<wid>/workspaces/<wid>/analytics`
                URL on each redirect (PR #524 Copilot review). The
                Navigate strips the workspace prefix and bounces to
                the new admin location; non-superadmins land at
                `/admin/analytics` and get the RequireSuperadmin 403
                stub, superadmins see the dashboard. */}
            <Route path="analytics" element={<Navigate to="/admin/analytics" replace />} />
            <Route path="analytics/runs/:runId" element={<Navigate to="/admin/analytics" replace />} />
          </Route>

          {/* Anything else — including legacy flat URLs from before
              #127 — redirects to the workspace-scoped equivalent. */}
          <Route path="*" element={<WorkspaceRedirect />} />
        </Route>
      </Route>
    </Routes>
  )
}

export default App
